import {
	Component,
	OnInit,
	ChangeDetectionStrategy,
	Input,
	ViewChild,
	SimpleChanges,
	AfterViewInit, ElementRef,
	EventEmitter,
	Output,
	HostListener,
	OnDestroy
} from '@angular/core';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { FormControl } from "@angular/forms";
import { map, Observable, startWith, Subject, takeUntil, tap } from "rxjs";
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MergeTagQuery } from "../../../../_core/state/merge-tag";
import { MergeTagDto } from "../../../../../../../api/src/creative-unit-package/models/merge-tag/merge-tag.dto";
import { isEqual } from "date-fns";
import { SortDirection } from "../../../../../../../api/src/_core/utils/utils.paging";
import { InputUtils } from "../input.utils";
import { registerCustomSizeFormat } from './modules/custom-size-style';
import { MergeTagPickerInputBaseComponent } from '../../merge-tag-picker/merge-tag-picker-input-base/merge-tag-picker-input-base.component';
import { MergeTagPickerOverlayService } from '../../merge-tag-picker/merge-tag-picker-overlay.service';

@Component({
	selector: 'app-wysiwyg-input',
	templateUrl: './wysiwyg-input.component.html',
	styleUrls: ['./wysiwyg-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class WysiwygInputComponent extends MergeTagPickerInputBaseComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('quillEditor', { static: true }) quillEditor!: QuillEditorComponent;
	@ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
	@ViewChild('hiddenInput') hiddenInput!: ElementRef<HTMLInputElement>;

	@Input() fullWidth: boolean = false;
	@Input() modules: QuillModules;
	@Input() showWritingTools: boolean = false;
	@Input() theme: 'bubble' | 'snow' = 'snow';
	@Input() toolbar: any[] | 'simple' | 'regular' | 'complex' = 'regular';
	@Input() bounds: HTMLElement | string = document.body;
	@Input() enableFontSizeInput: boolean = false;
	@Input() enableHtmlCodeEditor: boolean = false;
	@Output() valueChanged = new EventEmitter<string>();

	codeMode = false;
	interface: 'simple' | 'advanced' = 'simple';
	private removePasteListener?: () => void;
	private removeEditorKeydownListener?: () => void;
	private lastTriggerLength = 0;
	private destroy$ = new Subject<void>();

	private documentClickHandler = () => {
		let pickers = document.querySelectorAll('.ql-picker-options');
		pickers.forEach(picker => {
			let pickerRect = picker.getBoundingClientRect();
			let windowWidth = window.innerWidth;
			if (pickerRect.right > windowWidth - 10) {
				picker.classList.add('move-left');
			} else {
				picker.classList.remove('move-left');
			}
		});
	};


	// Simple toolbar - just basic formatting
	private simpleToolbar = [
		['bold', 'italic', 'underline'],
		['link']
	];

	// Regular toolbar - common formatting options
	private regularToolbar = [
		['bold', 'italic', 'underline', 'strike']
		['link'],
		[{ 'script': 'sub'}, { 'script': 'super' }]
	];

	// Complex toolbar - kitchen sink with all options
	private complexToolbar = [
		['bold', 'italic', 'underline', 'strike'],
		[{ 'script': 'sub'}, { 'script': 'super' }],
		[{ 'list': 'ordered'}, { 'list': 'bullet' }],
		['link']
	];

	get computedModules(): QuillModules {
		// If custom modules provided, use them
		if (this.modules) {
			return this.modules;
		}

		// Determine toolbar configuration
		let toolbarConfig: any;

		if (typeof this.toolbar === 'string') {
			// Use predefined complexity level
			switch (this.toolbar) {
				case 'simple':
					toolbarConfig = this.simpleToolbar;
					break;
				case 'complex':
					toolbarConfig = this.complexToolbar;
					break;
				case 'regular':
				default:
					toolbarConfig = this.regularToolbar;
					break;
			}
		} else {
			// Use custom toolbar array
			toolbarConfig = this.toolbar;
		}

		return {
			toolbar: toolbarConfig
		};
	}

	@Input() maxLength: number;

	@Input() showAllOptionsWhenEmpty: boolean = false;
	@Input() order: SortDirection | null = SortDirection.ASC;
	@Input() prepareValueFn: (args: any) => any = null;

	@Input() showSuggestCondition: (args: any) => any = (value: string | MergeTagDto) => {
		return (value?.['value'] || value)?.includes('{');
	};

	public autocompleteCtrl = new FormControl('');
	public filteredTags$: Observable<MergeTagDto[]>;

	private quill: any;
	private lastTriggerIndex: number = -1;

	constructor(
		private mergeTagQuery: MergeTagQuery,
		public mergeTagOverlayService: MergeTagPickerOverlayService,
		private el: ElementRef
	) {
		super(mergeTagOverlayService);
	}

	ngOnInit() {
		super.ngOnInit();

		// Register custom font-size format before initializing Quill
		if (this.enableFontSizeInput) {
			registerCustomSizeFormat();
		}

		if (this.enableMergeTags) {
			this.optionValueKey = 'value';
			this.optionLabelKey = 'label';

			this.mergeTagQuery.selectAll() .pipe(takeUntil(this.destroy$)).subscribe(tags => {
				this.options = tags;
			});

			this.filteredTags$ = this.listenToAutosuggestChanges();
		}
	}

	ngAfterViewInit() {
		document.addEventListener('click', this.documentClickHandler);
	}

	override getInputElement(): HTMLElement {
		return this.quill?.root as HTMLElement;
	}

	override getInputWrapperElement(): HTMLElement {
		return this.el?.nativeElement as HTMLElement;
	}

	override getInputValue(): string {
		return this.quill?.getText();
	}

	override getCursorIndex(): number {
		return this.quill.getSelection()?.index ?? 0;
	}

	onWritingToolsChange(event: string) {
		this.form.patchValue({
			[this.controlName]: event
		});
		this.emitValueChange(event);
	}

	@HostListener('keydown.enter', ['$event'])
	onEnterKey(event: KeyboardEvent) {
		// Prevent default Enter key behavior
		event.preventDefault();
	}

	/*
 	* ngOnChanges is customized to prevent the base class's ngOnChanges logic
 	* from interfering with the user's input. Specifically, updates to `currentValue`
 	* are blocked when the form control is dirty, ensuring that ongoing user input is
	* not overwritten by changes from the base class.
	* 
	* However, we need to allow updates when the value actually changes significantly
	* (e.g., when switching between units), not just when the form is being re-rendered.
 	*/
	ngOnChanges(changes: SimpleChanges) {
		// If currentValue changed and we have a form
		if (changes.currentValue && this.form?.controls?.[this.controlName]) {
			const previousValue = changes.currentValue.previousValue;
			const currentValue = changes.currentValue.currentValue;
			const formValue = this.form.controls[this.controlName].value;
			
			// Allow update if:
			// 1. Form is not dirty (user hasn't typed)
			// 2. The new value is significantly different from both the form value and previous value
			//    (indicates switching context, not just a re-render)
			const isDirty = this.form.controls[this.controlName].dirty;
			const isSignificantChange = currentValue !== previousValue && currentValue !== formValue;
			
			// Debug logging
			console.log('WYSIWYG ngOnChanges:', {
				label: this.label,
				isDirty,
				isSignificantChange,
				previousValue,
				currentValue,
				formValue
			});
			
			if (isDirty && !isSignificantChange) {
				// Block update only if form is dirty AND it's not a significant change
				console.log('WYSIWYG: Blocking update - form is dirty and no significant change');
				return;
			}
			
			// If it's a significant change, mark form as pristine so the new value takes effect
			if (isSignificantChange) {
				console.log('WYSIWYG: Significant change detected, updating value');
				if (isDirty) {
					this.form.controls[this.controlName].markAsPristine();
				}
			}
		}
		
		super.ngOnChanges(changes);
	}

	showAutocomplete() {
		if (!this.autocompleteTrigger) return;
		if (!this.autocompleteTrigger.panelOpen) {
			this.autocompleteTrigger.openPanel();
		} else {
			this.quill.focus();
		}
	}

	selectAutosuggest(event: MatAutocompleteSelectedEvent) {
		if (event.option.value) {
			const tag = event.option.value;

			if (this.lastTriggerIndex !== -1) {
				const deleteLength = this.lastTriggerLength || 2;
				this.quill.deleteText(this.lastTriggerIndex, deleteLength);
				this.lastTriggerLength = 0;
				this.quill.insertText(this.lastTriggerIndex, tag.value);
				const inserted = tag.value;
				const start = this.lastTriggerIndex;
				// Strip trailing newline from Quill's getText()
				const html = this.quill.root.innerHTML;
				this.changeValue(html);
				const caret = Math.min(start + inserted.length, this.quill.getLength());

				this.lastTriggerIndex = -1;
				this.autocompleteTrigger.closePanel();
				const restoreCaret = () => {
					this.quill.setSelection(caret, 0, 'silent');
					this.quill.focus();
				};

				setTimeout(() => restoreCaret(), 0);
			}

			this.autocompleteCtrl.setValue(null);
		}
	}

	private listenToQuillChanges() {
		this.quill.on('text-change', (delta, oldDelta, source) => {

			// Remove double newlines at the end caused by Quill behavior
			if (source === 'user' && oldDelta?.ops?.[0].insert === '\n' && this.quill.getText()?.endsWith('\n\n')) {
				this.quill.deleteText(this.quill.getText().length - 1, 1);
			}

			if (this.enableMergeTags) {
				const text = this.quill.getText();
				const cursorPos = this.quill.getSelection()?.index ?? 0;
				const textUpToCursor = text.slice(0, cursorPos);
				const lastOpeningAt = textUpToCursor.lastIndexOf('{{');
				const lastClosingAt = textUpToCursor.lastIndexOf('}}');

				if (lastOpeningAt !== -1 && lastOpeningAt > lastClosingAt) {
					this.lastTriggerIndex = lastOpeningAt;
					const currentQuery = text.slice(lastOpeningAt, cursorPos);
					this.lastTriggerLength = cursorPos - lastOpeningAt;
					this.autocompleteCtrl.setValue(currentQuery, { emitEvent: true });
					this.showAutocomplete();
				} else {
					this.lastTriggerLength = 0;

					if (this.autocompleteTrigger.panelOpen) {
						this.closeAutocomplete();
					}
				}
			}

			if (this.maxLength) this.trimTextToMaxLength();
		});
	}

	private trimTextToMaxLength() {
		let shouldReturnFocusToEditor: boolean = false;

		try {
			// Note: getText() includes trailing newline, but we need to count it for length checking
			const textLength = this.quill.getText()?.length;
			if (textLength <= this.maxLength) return;

			shouldReturnFocusToEditor = this.quill.hasFocus();

			this.quill.disable();

			this.quill.deleteText(this.maxLength, textLength - this.maxLength);
			// emitValueChange will strip the trailing newline via override
			this.emitValueChange(this.quill.getText());
		} finally {
			this.quill.enable();

			if (shouldReturnFocusToEditor) {
				this.quill.setSelection(this.maxLength, 0);
				this.quill.focus();
			}
		}
	}

	private listenToAutosuggestChanges() {
		return this.autocompleteCtrl.valueChanges.pipe(
			startWith(null),
			map((item: string | null) => item?.['value'] || item),
			tap(item => console.log('item', item)),
			map((item: string | null) => {
				// Remove the curly brackets for an improved search
				item = item?.replace(/[{}]/g, '');

				if (!item) {
					return this.options || [];
				}

				return (this.options || []).filter((option: any) => {
					let isAcceptedByFilter: boolean;

					if (this.optionValueKey) {
						isAcceptedByFilter = item
							? option[this.optionValueKey]
								.toLowerCase()
								.includes(item[this.optionValueKey]?.toLowerCase() || item.toLowerCase())
							: this.showAllOptionsWhenEmpty;

					} else {
						isAcceptedByFilter = item ? isEqual(item, option) : this.showAllOptionsWhenEmpty;
					}

					return isAcceptedByFilter;
				});
			}),
			map((options: any[]) => InputUtils.conditionalSort(options, this.order, this.optionValueKey))
		);
	}

	private closeAutocomplete() {
		this.autocompleteTrigger.closePanel();
		this.quill.focus();
	}

	private forwardKeydownToAutocomplete(event: KeyboardEvent) {
		if (!this.enableMergeTags || !this.autocompleteTrigger?.panelOpen) return;
		const navigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'];
		if (!navigationKeys.includes(event.key)) return;
		event.preventDefault();
		event.stopPropagation();
		(this.autocompleteTrigger as any)._handleKeydown(event);
	}

	onMonacoChange(value: string): void {
		const ctrl = this.form?.get(this.controlName);
		if (!ctrl) return;
	  
		ctrl.setValue(value, { emitEvent: true });
		
		this.valueChanged?.emit(value);
	}
	
	toggleInterface(): void {
		this.interface = this.interface === 'simple' ? 'advanced' : 'simple';
		this.codeMode = (this.interface === 'advanced');
	}

	onFontSizeChange(size: string) {
		// Handle font size change event if needed
		console.log('Font size changed to:', size);
	}

	onEditorCreated(quill: any) {
		this.quill = quill;
		this.listenToQuillChanges();

		const root = quill.root as HTMLElement;
		this.removePasteListener?.();
		this.removeEditorKeydownListener?.();

		const onPaste = (e: ClipboardEvent) => {
			if (!e.clipboardData) return;

			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();

			const text = e.clipboardData.getData('text/plain') ?? '';
			const sel = quill.getSelection(true);
			const start = sel ? sel.index : quill.getLength();

			// remove double newlines by removing the first newline in a sequence of newlines
			const cleanedText = text.replace(/\n(\n+)/g, '$1');

			quill.deleteText(start, sel?.length ?? 0, 'user');
			quill.insertText(start, cleanedText, 'user');

			requestAnimationFrame(() => {
				quill.setSelection(start + text.length, 0, 'silent');
			});
		};
		const keydownHandler = (event: KeyboardEvent) => this.forwardKeydownToAutocomplete(event);
		root.addEventListener('keydown', keydownHandler);

		root.addEventListener('paste', onPaste, { passive: false, capture: true });

		this.removePasteListener = () => root.removeEventListener('paste', onPaste, { capture: true });
		this.removeEditorKeydownListener = () => root.removeEventListener('keydown', keydownHandler);

		// Fix tooltip positioning to keep it within viewport
		this.adjustTooltipPosition();
	}

	private adjustTooltipPosition() {
		// Use MutationObserver to detect when tooltip appears and reposition it
		const container = this.quill?.container;
		if (!container) return;

		const observer = new MutationObserver(() => {
			const tooltip = container.querySelector('.ql-tooltip') as HTMLElement;
			if (tooltip && (tooltip.classList.contains('ql-editing') || tooltip.classList.contains('ql-hidden') === false)) {
				// Small delay to ensure Quill has finished positioning
				setTimeout(() => {
					const rect = tooltip.getBoundingClientRect();
					const viewportWidth = window.innerWidth;
					const viewportHeight = window.innerHeight;
					const isFlipped = tooltip.classList.contains('ql-flip');
					const isEditing = tooltip.classList.contains('ql-editing');

					// Only adjust if tooltip is off-screen
					const isOffScreenLeft = rect.left < 20;
					const isOffScreenRight = rect.right > viewportWidth - 20;
					const isOffScreenTop = rect.top < 20;
					const isOffScreenBottom = rect.bottom > viewportHeight - 20;

					if (!isOffScreenLeft && !isOffScreenRight && !isOffScreenTop && !isOffScreenBottom) {
						// Tooltip is fully visible, no adjustment needed
						return;
					}

					// Get bounds container rect for relative positioning
					const boundsRect = container.getBoundingClientRect();
					
					// Calculate adjustments
					if (isOffScreenLeft || isOffScreenRight) {
						// Horizontal adjustment needed
						if (isOffScreenRight) {
							// Off right edge - position from right
							const offsetFromRight = viewportWidth - boundsRect.right;
							tooltip.style.left = 'auto';
							tooltip.style.right = `${Math.max(20, offsetFromRight + 20)}px`;
							tooltip.style.transform = isFlipped ? 'translateY(-100%)' : 'none';
						} else if (isOffScreenLeft) {
							// Off left edge - position from left
							const offsetFromLeft = Math.max(20, 20 - boundsRect.left);
							tooltip.style.left = `${offsetFromLeft}px`;
							tooltip.style.right = 'auto';
							tooltip.style.transform = isFlipped ? 'translateY(-100%)' : 'none';
						}
					}

					// Vertical adjustment
					if (isOffScreenTop) {
						// Off top edge - position below with padding
						const offsetFromTop = Math.max(20, 20 - boundsRect.top);
						tooltip.style.top = `${offsetFromTop}px`;
					} else if (isOffScreenBottom) {
						// Off bottom edge - position above with padding
						const offsetFromBottom = viewportHeight - boundsRect.bottom;
						tooltip.style.top = 'auto';
						tooltip.style.bottom = `${Math.max(20, offsetFromBottom + 20)}px`;
					}
				}, 10);
			}
		});

		observer.observe(container, {
			attributes: true,
			childList: true,
			subtree: true,
			attributeFilter: ['class', 'style']
		});

		// Store observer reference for cleanup
		(this as any).tooltipObserver = observer;
	}

	/**
	 * Override emitValueChange to strip Quill's trailing newline character.
	 * Quill's getText() always includes a trailing "\n" that we need to remove
	 * to properly detect when the user has deleted all content.
	 */
	override emitValueChange(value: any): void {
		// null is planned value, everything else is properly parsed and passes
		if (value === undefined) {
			return;
		}
		// If value is a string and ends with newline, strip it
		const cleanValue = typeof value === 'string' && value.endsWith('\n') 
			? value.slice(0, -1) 
			: value;

		// Only emit when the value has actually changed
		if (cleanValue === this.currentValue) {
			return;
		}

		super.emitValueChange(cleanValue);
	}

	override focusInputAt(position: number) {
		const inputEl: HTMLInputElement | undefined = this.getInputElement() as HTMLInputElement;
		if (!inputEl) {
			return;
		}

		this.quill.setSelection(position, 0, 'silent');
		this.quill.focus();
		this.pendingCursorPosition = position;
	}

	override onMergeTagSelected(tag: string) {
		const cursorPos = this.pendingCursorPosition;
		this.quill.insertText(cursorPos, tag, 'user');

		const html = this.quill.root.innerHTML;
		this.emitValueChange(html);

		// TODO: Extend to handle slashIndex if needed
		this.pendingSlashIndex = null;
		this.pendingCursorPosition = cursorPos + tag.length;
		this.inputHistory$.next(html);

		const caret = Math.min(cursorPos + tag.length, this.quill.getLength());

		setTimeout(() => {
			this.focusInputAt(caret);
			this.updateOverlayAnchor();
		}, 0);
	}

	ngOnDestroy() {
		super.ngOnDestroy();

		document.removeEventListener('click', this.documentClickHandler);
		this.removePasteListener?.();
		this.removePasteListener = undefined;

		// Clean up tooltip position observer
		if ((this as any).tooltipObserver) {
			(this as any).tooltipObserver.disconnect();
			(this as any).tooltipObserver = undefined;
		}
		this.removeEditorKeydownListener?.();
		this.removeEditorKeydownListener = undefined;

		this.destroy$.next();
		this.destroy$.complete();
	}
}
